import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../../core/auth/services/auth.service';
import {
  IUser,
  INewUser,
  IUserChangesResponse,
  IUserProfile,
} from '@shared/models';
import { ISessionToken } from '@core/models';
import config from '../../../config';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apiUrl = config.API_GATEWAY_ENDPOINT;
  prid = '';
  userCache: IUserProfile | null = null;

  constructor(
    readonly http: HttpClient,
    readonly authService: AuthService,
  ) {
    this.authService.amplifyGetUserSession().forEach((session: any) => {
      this.prid = this._extractPridFromSession(session);
    });
  }

  async fetchUser(): Promise<IUserProfile> {
    if (this.userCache) {
      return this.userCache;
    }

    try {
      const [response] = await lastValueFrom(
        this.http.get<IUserProfile[]>(
          `${this.apiUrl}/user/projects/${this.prid}`,
        ),
      );
      const profile = response
        ? { ...response, projects: this._normalizeProjects(response.projects) }
        : response;
      // Save to cache
      this.userCache = profile;
      return profile;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async fetchUsers(): Promise<IUser[]> {
    try {
      const response = await lastValueFrom(
        this.http.get<IUser[]>(`${this.apiUrl}/users`),
      );
      return (response ?? []).map((user) => ({
        ...user,
        projects: this._normalizeProjects(user.projects),
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // The API may return projects as a JSON string with DynamoDB attribute values, e.g. '[{"S":"rdpp"}]'
  _normalizeProjects(projects: unknown): string[] {
    let value = projects;

    if (typeof value === 'string') {
      const raw = value;
      try {
        value = JSON.parse(raw);
      } catch {
        return raw.trim() ? [raw] : [];
      }
    }

    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) =>
        typeof item === 'string' ? item : (item?.S ?? item?.s ?? ''),
      )
      .filter((item: string) => !!item);
  }

  async createUser(user: INewUser): Promise<IUserChangesResponse> {
    try {
      const response = await lastValueFrom(
        this.http.post<IUserChangesResponse>(`${this.apiUrl}/users`, user),
      );
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateUser(user: INewUser): Promise<IUserChangesResponse> {
    try {
      const response = await lastValueFrom(
        this.http.put<IUserChangesResponse>(`${this.apiUrl}/users`, user),
      );
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  _extractPridFromSession(session: ISessionToken): string {
    if (session?.tokens?.accessToken?.payload?.username) {
      const pridToken = session.tokens.accessToken.payload.username;
      const email = pridToken.split('@')[0];
      return email.split('_')[1] || email;
    }
    return '';
  }
}
